import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, MapPin, Radio, Wifi, WifiOff, Battery, ArrowRight, Route } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

// Phase G (Feb 2026): Live tracking dashboard for Management/Super Admin.
// Table view (map can be added later). Auto-refreshes every 20s.
const STATUS_STYLE = {
  active:    { dot: "bg-[#34C759]", text: "text-[#34C759]", label_en: "Active",   label_ta: "செயலில்" },
  idle:      { dot: "bg-[#FF9500]", text: "text-[#FF9500]", label_en: "Idle",     label_ta: "ஓய்வில்" },
  inactive:  { dot: "bg-[#FF3B30]", text: "text-[#FF3B30]", label_en: "Inactive", label_ta: "செயல்பாடில்லை" },
  no_gps:    { dot: "bg-gray-400",  text: "text-gray-500",  label_en: "No GPS",   label_ta: "GPS இல்ல" },
};

export default function LiveTracking() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [data, setData] = useState({ users: [], counts: {}, as_of: null });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    api.get("/tracking/live")
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  const filtered = data.users.filter(u => {
    if (filter === "all") return true;
    if (filter === "active") return u.status === "active";
    if (filter === "idle") return u.status === "idle";
    if (filter === "inactive") return u.status === "inactive";
    if (filter === "no_gps") return u.status === "no_gps";
    if (filter === "salesman") return u.role === "salesman";
    if (filter === "manager") return u.role === "manager";
    return true;
  });

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={lang === "ta" ? "நேரடி கண்காணிப்பு" : "Live Tracking"} onBack={() => nav("/management")} />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5 fade-up">

        {/* Route History CTA */}
        <button
          data-testid="lt-route-history-btn"
          onClick={() => nav("/management/route-history")}
          className="w-full bg-gradient-to-r from-[#5856D6] to-[#0055FF] text-white rounded-2xl p-4 shadow-lg hover:shadow-xl active:scale-[0.99] transition flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-2xl bg-white/15 grid place-items-center shrink-0">
            <Route className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">
              {lang === "ta" ? "வழி வரலாறு" : "Route History"}
            </div>
            <div className="heading-font font-bold text-base">
              {lang === "ta" ? "நபர் + தேதி தேர்ந்தெடுத்து வழி பார்க்க" : "Pick a person + date to see their full route"}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 opacity-80" />
        </button>

        {/* Top counts */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="lt-counts">
          <CountCard color="bg-[#34C759]" icon={Activity}      label={lang === "ta" ? "செயலில்" : "Active"}        value={data.counts.active || 0} />
          <CountCard color="bg-[#FF9500]" icon={Wifi}           label={lang === "ta" ? "ஓய்வில்" : "Idle"}           value={data.counts.idle || 0} />
          <CountCard color="bg-[#FF3B30]" icon={WifiOff}        label={lang === "ta" ? "செயல்பாடில்லை" : "Inactive"} value={data.counts.inactive || 0} />
          <CountCard color="bg-gray-400" icon={Radio}          label={lang === "ta" ? "GPS இல்ல" : "No GPS"}        value={data.counts.no_gps || 0} />
          <CountCard color="bg-purple-600" icon={AlertTriangle} label={lang === "ta" ? "சந்தேகமான" : "Suspicious"}   value={data.counts.suspicious || 0} />
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2">
          {["all", "active", "idle", "inactive", "no_gps", "salesman", "manager"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`lt-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${filter === f ? "bg-[#0055FF] text-white shadow" : "bg-white text-[#86868B] border border-gray-200 hover:bg-gray-50"}`}
            >
              {f === "all" ? (lang === "ta" ? "எல்லாம்" : "All") :
               f === "active" ? (lang === "ta" ? "செயலில்" : "Active") :
               f === "idle" ? (lang === "ta" ? "ஓய்வில்" : "Idle") :
               f === "inactive" ? (lang === "ta" ? "செயல்பாடில்லை" : "Inactive") :
               f === "no_gps" ? (lang === "ta" ? "GPS இல்ல" : "No GPS") :
               f === "salesman" ? (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman") :
               (lang === "ta" ? "மேனேஜர்" : "Manager")}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="heading-font font-bold text-lg">
              {lang === "ta" ? "களத்தில் உள்ளவர்கள்" : "Field Activity"} ({filtered.length})
            </h2>
            {data.as_of && (
              <div className="text-xs text-[#86868B]">{lang === "ta" ? "புதுப்பிக்கப்பட்டது" : "Updated"}: {new Date(data.as_of).toLocaleTimeString()}</div>
            )}
          </div>
          {loading ? (
            <div className="p-8 text-center text-[#86868B]">{t("loading")}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-[#86868B]" data-testid="lt-empty">
              {lang === "ta" ? "எவரும் இல்ல" : "Nobody to show"}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map(u => {
                const s = STATUS_STYLE[u.status] || STATUS_STYLE.no_gps;
                return (
                  <button
                    key={u.user_id}
                    onClick={() => nav(`/management/route-history?user_id=${u.user_id}`)}
                    data-testid={`lt-row-${u.user_id}`}
                    className="w-full p-4 text-left hover:bg-gray-50/40 transition flex items-center gap-3"
                  >
                    <div className={`w-3 h-3 rounded-full ${s.dot} animate-pulse shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="heading-font font-bold text-[#1D1D1F]">{u.name}</div>
                        <span className={`pill ${u.role === "manager" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"} text-[10px]`}>
                          {u.role === "manager" ? (lang === "ta" ? "மேனேஜர்" : "Manager") : (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman")}
                        </span>
                        <span className={`text-[11px] font-bold ${s.text}`}>● {lang === "ta" ? s.label_ta : s.label_en}</span>
                      </div>
                      <div className="text-xs text-[#86868B] mt-1 flex flex-wrap gap-3">
                        {u.last_seen_minutes_ago != null && <span>{u.last_seen_minutes_ago}m ago</span>}
                        <span>📦 {u.today_boxes || 0} {t("boxes")}</span>
                        <span>{u.today_delivered || 0}/{u.today_orders || 0} delivered</span>
                        {u.battery_level != null && (
                          <span className="flex items-center gap-1"><Battery className="w-3 h-3" /> {Math.round(u.battery_level)}%</span>
                        )}
                      </div>
                    </div>
                    {u.lat != null && u.lng != null ? (
                      <a
                        href={`https://www.openstreetmap.org/?mlat=${u.lat}&mlon=${u.lng}&zoom=17`}
                        target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#0055FF] hover:underline text-xs font-bold flex items-center gap-1 shrink-0"
                      >
                        <MapPin className="w-3.5 h-3.5" /> {lang === "ta" ? "வரைபடம்" : "Map"}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 shrink-0">—</span>
                    )}
                    <ArrowRight className="w-4 h-4 text-[#86868B] shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CountCard = ({ color, icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{label}</div>
      <div className="heading-font text-2xl font-bold text-[#1D1D1F]">{value}</div>
    </div>
  </div>
);
